import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  StructurePredictionModel,
  getModelDisplayName,
  MODEL_EGRESSES_DATA,
} from 'molbio/proteins/util/StructurePredictionConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

const MODEL_LINKS: Record<StructurePredictionModel, string> = {
  [StructurePredictionModel.ALPHAFOLD]: 'https://deepmind.google/science/alphafold/',
  [StructurePredictionModel.CHAI1]: 'https://www.chaidiscovery.com/news/introducing-chai-1',
  [StructurePredictionModel.BOLTZ2]: 'https://boltz.bio/boltz2',
};

interface ModelSelectorProps {
  selectedModel: StructurePredictionModel;
  onModelChange: (model: StructurePredictionModel) => void;
  disabled?: boolean;
  className?: string;
}

function BetaBadge(): JSX.Element {
  return <Tag size={TagSize.SMALL} color={TagColor.PURPLE} text="PREVIEW" />;
}

export default function ModelSelector({
  selectedModel,
  onModelChange,
  disabled = false,
  className,
}: ModelSelectorProps): JSX.Element {
  // All users have access to AlphaFold2.
  const configEnabledModels: Array<StructurePredictionModel> = [StructurePredictionModel.ALPHAFOLD];
  // Chai and Boltz2 are controlled by the MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS flag.
  if (BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS) {
    configEnabledModels.push(StructurePredictionModel.CHAI1);
    configEnabledModels.push(StructurePredictionModel.BOLTZ2);
  }

  return (
    <div className={className}>
      <div className={css(styles.label)}>Model</div>
      <SelectDropdown
        displayName={
          <div className={css(styles.rowContent)}>
            <span>{getModelDisplayName(selectedModel)}</span>
          </div>
        }
        disabled={disabled}
        menu={
          <Menu width={MenuWidth.FULL} value={selectedModel}>
            {configEnabledModels.map((model) => (
              <Option
                key={model}
                value={model}
                onSelect={() => {
                  onModelChange(model);
                }}
              >
                <div className={css(styles.rowContent)}>
                  <span>{getModelDisplayName(model)}</span>
                  {[
                    StructurePredictionModel.CHAI1,
                    StructurePredictionModel.BOLTZ2,
                    StructurePredictionModel.ALPHAFOLD,
                  ].includes(model) && <BetaBadge />}
                </div>
              </Option>
            ))}
          </Menu>
        }
      />
      <div className={css(styles.helpText)}>
        <span>
          MSAs (multi-sequence alignments) increase accuracy by providing evolutionary context to the
          structure prediction model, but will add time to the prediction.{' '}
          {getModelDisplayName(StructurePredictionModel.ALPHAFOLD)} uses an MSA. Support for MSAs for{' '}
          {getModelDisplayName(StructurePredictionModel.CHAI1)} and{' '}
          {getModelDisplayName(StructurePredictionModel.BOLTZ2)} coming soon.
        </span>
        {!MODEL_EGRESSES_DATA[selectedModel] && (
          <>
            <br />
            <br />
            <span>
              {getModelDisplayName(selectedModel)} is run on Benchling's infrastructure. Your data is not sent
              to third parties.
            </span>
          </>
        )}
      </div>
      <div className={css(styles.linkContainer)}>
        <a
          href={MODEL_LINKS[selectedModel]}
          target="_blank"
          rel="noopener noreferrer"
          className={css(styles.modelLink)}
        >
          Learn more about {getModelDisplayName(selectedModel)}
        </a>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  label: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
    marginBottom: '6px',
    color: GRAY_DARKER,
  },
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginTop: '8px',
    maxWidth: '600px',
  },
  linkContainer: {
    marginTop: '8px',
  },
  modelLink: {
    ...FONT_PROXIMA_13,
    color: '#0066cc',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  previewBadge: {
    marginLeft: '8px',
  },
});

export {MODEL_LINKS};
